/**
 * ArbitrageWidget — Real-time arbitrage opportunity detection.
 * Derived directly from live_odds so it does not depend on legacy RPC functions.
 * Prefix: arb-
 */
import { useState, useEffect, useCallback, useMemo } from 'react'
import { adaptEngineArbs, fetchCrossbookEdge, getEdgeFeedProof } from '../lib/edgeData'
import { getBookLogo } from '../lib/logoMaps'
import {
  getNormalizedTeamDisplay,
  normalizeLeagueCode,
} from '../lib/oddsNormalization'
import { RefreshCw, DollarSign, TrendingUp, AlertCircle, Zap } from 'lucide-react'
import './ArbitrageWidget.css'

const LEAGUES = [
  { id: 'ALL', label: 'All' },
  { id: 'NFL', label: 'NFL' },
  { id: 'NBA', label: 'NBA' },
  { id: 'MLB', label: 'MLB' },
  { id: 'NHL', label: 'NHL' },
  { id: 'CFB', label: 'CFB' },
  { id: 'CBB', label: 'MCBB' },
  { id: 'NCAAW', label: 'WCBB' },
  { id: 'SOCCER', label: 'Soccer' },
  { id: 'MMA', label: 'MMA' },
]
const ARB_REFRESH_MS = 30000

function formatOdds(american) {
  if (american == null) return '—'
  return american > 0 ? `+${american}` : `${american}`
}

function formatParticipants(arb) {
  const awayDisplay = getNormalizedTeamDisplay(arb.away_team, arb.league)
  const homeDisplay = getNormalizedTeamDisplay(arb.home_team, arb.league)
  if (awayDisplay && homeDisplay) return `${awayDisplay} @ ${homeDisplay}`
  return arb.participants || `${arb.away_team} @ ${arb.home_team}`
}

export default function ArbitrageWidget() {
  const [engineArbs, setEngineArbs] = useState([])
  const [proof, setProof] = useState(null)
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState(null)
  const [league, setLeague] = useState('ALL')
  const [minProfit, setMinProfit] = useState(0)
  const [expandedArb, setExpandedArb] = useState(null)
  const [stake, setStake] = useState(100)

  const fetchArbs = useCallback(async () => {
    try {
      setLoading(true)
      const payload = await fetchCrossbookEdge()
      setEngineArbs(adaptEngineArbs(payload))
      setProof(getEdgeFeedProof(payload))
      setError(null)
    } catch (e) {
      setError(e.message)
    } finally {
      setLoading(false)
    }
  }, [])

  useEffect(() => {
    fetchArbs()
    const interval = setInterval(fetchArbs, ARB_REFRESH_MS)
    return () => clearInterval(interval)
  }, [fetchArbs])

  const arbs = useMemo(() => engineArbs.filter((arb) => {
    if (league !== 'ALL' && normalizeLeagueCode(arb.league) !== league) return false
    return Number(arb.profit_pct || 0) >= minProfit
  }), [engineArbs, league, minProfit])

  const proofAgeMs = proof?.capturedAt ? Math.max(0, Date.now() - new Date(proof.capturedAt).getTime()) : null
  const proofIsStale = proofAgeMs == null || proofAgeMs > 5 * 60_000

  // Calculate stakes for a given arb
  function calcStakes(arb, totalStake) {
    const imp1 = arb.odds1 > 0 ? 100 / (arb.odds1 + 100) : Math.abs(arb.odds1) / (Math.abs(arb.odds1) + 100)
    const imp2 = arb.odds2 > 0 ? 100 / (arb.odds2 + 100) : Math.abs(arb.odds2) / (Math.abs(arb.odds2) + 100)
    const totalImplied = imp1 + imp2
    const stake1 = (totalStake * imp1) / totalImplied
    const stake2 = (totalStake * imp2) / totalImplied

    const payout1 = arb.odds1 > 0
      ? stake1 * (arb.odds1 / 100 + 1)
      : stake1 * (100 / Math.abs(arb.odds1) + 1)
    const payout2 = arb.odds2 > 0
      ? stake2 * (arb.odds2 / 100 + 1)
      : stake2 * (100 / Math.abs(arb.odds2) + 1)

    const profit = Math.min(payout1, payout2) - totalStake
    return { stake1: stake1.toFixed(2), stake2: stake2.toFixed(2), profit: profit.toFixed(2), payout: Math.min(payout1, payout2).toFixed(2) }
  }

  return (
    <div className="widget arb-widget">
      <div className="widget-header">
        <h3 className="widget-title">
          <Zap size={16} />
          Arbitrage Scanner
          {arbs.length > 0 && (
            <span className="arb-count-badge">{arbs.length} found</span>
          )}
        </h3>
        <button className="widget-refresh-btn" onClick={fetchArbs} title="Refresh">
          <RefreshCw size={14} className={loading ? 'arb-spinner' : ''} />
        </button>
      </div>

      {proof && (
        <div className={`arb-source-proof ${proofIsStale ? 'is-stale' : 'is-live'}`}>
          <span>Cross-book edge engine</span>
          <span>{proof.books} books</span>
          <span>{proof.markets} married markets</span>
          <span>Captured {new Date(proof.capturedAt).toLocaleString()}</span>
          <span>{Math.round(proofAgeMs / 60_000)}m old</span>
        </div>
      )}

      {/* League pills */}
      <div className="arb-filters">
        <div className="arb-filter-row">
          {LEAGUES.map(l => {
            const cnt = l.id === 'ALL' ? arbs.length : arbs.filter(a => normalizeLeagueCode(a.league) === l.id).length
            if (l.id !== 'ALL' && cnt === 0 && league !== l.id) return null
            return (
              <button
                key={l.id}
                className={`arb-pill ${league === l.id ? 'active' : ''}`}
                onClick={() => setLeague(l.id)}
              >
                {l.label}
                {cnt > 0 && <span className="arb-pill-count">{cnt}</span>}
              </button>
            )
          })}
        </div>
        <div className="arb-filter-row">
          <div className="arb-min-profit">
            <span>Min:</span>
            <input
              type="number"
              className="arb-input"
              value={minProfit}
              onChange={e => setMinProfit(Number(e.target.value) || 0)}
              step="0.1"
              min="0"
              max="20"
            />
            <span>%</span>
          </div>
          <div className="arb-stake-input">
            <DollarSign size={12} />
            <input
              type="number"
              className="arb-input"
              value={stake}
              onChange={e => setStake(Number(e.target.value) || 100)}
              step="10"
              min="1"
            />
          </div>
        </div>
      </div>

      {error && <div className="arb-error">{error}</div>}

      {loading && arbs.length === 0 ? (
        <div className="arb-loading">Scanning for arbitrage opportunities...</div>
      ) : arbs.length === 0 ? (
        <div className="arb-empty">
          <AlertCircle size={24} />
          <p>No arbitrage opportunities found</p>
          <span>Try lowering the minimum profit threshold or checking all sports</span>
        </div>
      ) : (
        <div className="arb-list">
          {arbs.map((arb, i) => {
            const isExpanded = expandedArb === i
            const stakes = calcStakes(arb, stake)
            const participantsDisplay = formatParticipants(arb)
            const leagueBadge = normalizeLeagueCode(arb.league)

            return (
              <div key={i} className={`arb-card ${isExpanded ? 'expanded' : ''}`}>
                <div className="arb-card-header" onClick={() => setExpandedArb(isExpanded ? null : i)}>
                  <div className="arb-card-left">
                    <span className="arb-profit-badge">
                      +{arb.profit_pct}%
                    </span>
                    <div className="arb-card-game">
                      <span className="arb-card-teams">
                        {participantsDisplay}
                      </span>
                      <span className="arb-card-market">{arb.market_normalized}</span>
                    </div>
                  </div>
                  <div className="arb-card-right">
                    <span className="arb-league-badge">{leagueBadge || 'ARB'}</span>
                  </div>
                </div>

                <div className="arb-card-sides">
                  <div className="arb-side">
                    <span className="arb-side-sel">{arb.selection1}</span>
                    <span className="arb-side-odds">{formatOdds(arb.odds1)}</span>
                    <span className="arb-side-book">
                      {getBookLogo(arb.book1) && <img src={getBookLogo(arb.book1)} alt="" className="arb-book-logo" onError={e => { e.target.style.display = 'none' }} />}
                      {arb.book1}
                    </span>
                  </div>
                  <span className="arb-vs">vs</span>
                  <div className="arb-side">
                    <span className="arb-side-sel">{arb.selection2}</span>
                    <span className="arb-side-odds">{formatOdds(arb.odds2)}</span>
                    <span className="arb-side-book">
                      {getBookLogo(arb.book2) && <img src={getBookLogo(arb.book2)} alt="" className="arb-book-logo" onError={e => { e.target.style.display = 'none' }} />}
                      {arb.book2}
                    </span>
                  </div>
                </div>

                {isExpanded && (
                  <div className="arb-card-details">
                    <div className="arb-calc-title">Stake Calculator (${stake} total)</div>
                    <div className="arb-calc-grid">
                      <div className="arb-calc-row">
                        <span className="arb-calc-label">{arb.selection1} @ {arb.book1}</span>
                        <span className="arb-calc-val">${stakes.stake1}</span>
                      </div>
                      <div className="arb-calc-row">
                        <span className="arb-calc-label">{arb.selection2} @ {arb.book2}</span>
                        <span className="arb-calc-val">${stakes.stake2}</span>
                      </div>
                      <div className="arb-calc-row arb-calc-profit">
                        <span className="arb-calc-label">Guaranteed Profit</span>
                        <span className="arb-calc-val">${stakes.profit}</span>
                      </div>
                    </div>
                    <div className="arb-implied">
                      <span>Implied: {arb.implied1} + {arb.implied2} = {arb.total_implied}</span>
                    </div>
                  </div>
                )}
              </div>
            )
          })}
        </div>
      )}
    </div>
  )
}
